import { useCart, useProduct } from '@chordcommerce/react-autonomy';
import useSWR, { KeyedMutator } from 'swr';

export type VariantWithStock = Queries.VariantDetailsFragmentFragment & {
  inStock: boolean;
};
type ProductUnion = Partial<Queries.ProductCardFragmentFragment> | Partial<Queries.ProductDetailsFragmentFragment>;

export type ProductWithStock = ProductUnion & {
  variants: VariantWithStock[];
};

const useProductAvailability = (
  product: ProductUnion | null
): {
  productWithStock: ProductWithStock | null;
  error: unknown;
  isFetching: boolean;
  revalidate: KeyedMutator<boolean[]>;
} => {
  const { getStockAvailability } = useProduct();
  const { cart } = useCart();

  const fetcher = async (skus: string[], channel?: string) => {
    const promises = skus.map((sku) => getStockAvailability({ sku, channel }));
    const results = await Promise.allSettled(promises);
    const data = results.map((result) => {
      if (result.status === 'fulfilled') {
        const { inStock, isBackorderable } = result.value;
        return inStock ? inStock : isBackorderable;
      }
      return false;
    });
    return data;
  };

  const variants = product?.variants as Queries.VariantDetailsFragmentFragment[];
  const isString = (value: string | null): value is string => typeof value === 'string';
  const skus = variants?.map((variant) => variant.sku).filter(isString);

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    [skus, cart.channel],
    ([skus, channel]) => fetcher(skus, channel),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      fallbackData: variants?.map(() => true),
    }
  );

  const productWithStock: ProductWithStock | null =
    data && data.length
      ? {
          ...product,
          variants: variants.map((variant, index) => ({
            ...variant,
            inStock: data[index],
          })),
        }
      : null;

  return { productWithStock, isFetching: isLoading || isValidating, error, revalidate: mutate };
};

export default useProductAvailability;
